import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { logoUrl } from '../mockData';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isChiSiamoOpen, setIsChiSiamoOpen] = useState(false);
  const chiSiamoRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (chiSiamoRef.current && !chiSiamoRef.current.contains(e.target)) {
        setIsChiSiamoOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
      setIsChiSiamoOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => scrollToSection('hero')}>
            <img
              src={logoUrl}
              alt="Assietica Società Cooperativa"
              className="h-32 sm:h-40 w-auto transition-transform duration-300 hover:scale-105"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {/* Chi Siamo Dropdown */}
            <div 
              ref={chiSiamoRef}
              className="relative"
              onMouseEnter={() => setIsChiSiamoOpen(true)}
              onMouseLeave={() => setIsChiSiamoOpen(false)}
            >
              <button
                onClick={() => setIsChiSiamoOpen((prev) => !prev)}
                className="flex items-center space-x-1 text-gray-700 hover:text-[#2B7A9F] font-medium transition-colors duration-300"
              >
                <span>Chi Siamo</span>
                <ChevronDown size={16} className={`transition-transform duration-300 ${isChiSiamoOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {isChiSiamoOpen && (
                <div className="absolute top-full left-0 w-56 pt-2">
                  <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-2 animate-fade-in">
                  <Link
                    to="/mission"
                    className="block px-4 py-3 text-gray-700 hover:bg-[#F9D424]/10 hover:text-[#E67E22] transition-colors duration-300"
                    onClick={() => setIsChiSiamoOpen(false)}
                  >
                    <span className="font-medium">La Nostra Mission</span>
                    <p className="text-xs text-gray-500 mt-1">Il cuore della nostra identità</p>
                  </Link>
                  <Link
                    to="/societa-benefit"
                    className="block px-4 py-3 text-gray-700 hover:bg-[#2B7A9F]/5 hover:text-[#2B7A9F] transition-colors duration-300"
                    onClick={() => setIsChiSiamoOpen(false)}
                  >
                    <span className="font-medium">Identità Benefit</span>
                    <p className="text-xs text-gray-500 mt-1">Il bene comune come fine</p>
                  </Link>
                  <Link
                    to="/societa-cooperativa"
                    className="block px-4 py-3 text-gray-700 hover:bg-[#5FB76E]/5 hover:text-[#5FB76E] transition-colors duration-300"
                    onClick={() => setIsChiSiamoOpen(false)}
                  >
                    <span className="font-medium">Anima Cooperativa</span>
                    <p className="text-xs text-gray-500 mt-1">La forza della mutualità</p>
                  </Link>
                  <div className="border-t border-gray-100 my-2"></div>
                  <button
                    onClick={() => { scrollToSection('team'); setIsChiSiamoOpen(false); }}
                    className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#3AAFA9] transition-colors duration-300"
                  >
                    <span className="font-medium">Il Nostro Team</span>
                    <p className="text-xs text-gray-500 mt-1">Le persone dietro Assietica</p>
                  </button>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => scrollToSection('benefici')}
              className="text-gray-700 hover:text-[#2B7A9F] font-medium transition-colors duration-300"
            >
              Servizi
            </button>
            <button
              onClick={() => scrollToSection('societa-benefit')}
              className="text-gray-700 hover:text-[#2B7A9F] font-medium transition-colors duration-300"
            >
              Società Benefit
            </button>
            <button
              onClick={() => scrollToSection('contatti')}
              className="bg-gradient-to-r from-[#E74C3C] to-[#E67E22] text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              Contattaci
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-gray-700 hover:text-[#2B7A9F] transition-colors duration-300"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden pb-6 animate-fade-in">
            <nav className="flex flex-col space-y-2">
              {/* Chi Siamo Mobile */}
              <div className="border-b border-gray-100 pb-2">
                <button
                  onClick={() => setIsChiSiamoOpen(!isChiSiamoOpen)}
                  className="flex items-center justify-between w-full text-left text-gray-700 hover:text-[#2B7A9F] font-medium py-2 transition-colors duration-300"
                >
                  <span>Chi Siamo</span>
                  <ChevronDown size={16} className={`transition-transform duration-300 ${isChiSiamoOpen ? 'rotate-180' : ''}`} />
                </button>
                {isChiSiamoOpen && (
                  <div className="pl-4 space-y-2 mt-2">
                    <Link
                      to="/mission"
                      className="block text-gray-600 hover:text-[#E67E22] py-2 transition-colors duration-300"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      → La Nostra Mission
                    </Link>
                    <Link
                      to="/societa-benefit"
                      className="block text-gray-600 hover:text-[#2B7A9F] py-2 transition-colors duration-300"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      → Identità Benefit
                    </Link>
                    <Link
                      to="/societa-cooperativa"
                      className="block text-gray-600 hover:text-[#5FB76E] py-2 transition-colors duration-300"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      → Anima Cooperativa
                    </Link>
                    <button
                      onClick={() => scrollToSection('team')}
                      className="block text-gray-600 hover:text-[#3AAFA9] py-2 transition-colors duration-300"
                    >
                      → Il Nostro Team
                    </button>
                  </div>
                )}
              </div>
              
              <button
                onClick={() => scrollToSection('benefici')}
                className="text-left text-gray-700 hover:text-[#2B7A9F] font-medium py-2 transition-colors duration-300"
              >
                Servizi
              </button>
              <button
                onClick={() => scrollToSection('societa-benefit')}
                className="text-left text-gray-700 hover:text-[#2B7A9F] font-medium py-2 transition-colors duration-300"
              >
                Società Benefit
              </button>
              <button
                onClick={() => scrollToSection('contatti')}
                className="bg-gradient-to-r from-[#E74C3C] to-[#E67E22] text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 text-left mt-2"
              >
                Contattaci
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
